// Système d'A/B Testing pour optimiser les conversions

export interface ConversionGoal {
  id: string;
  name: string;
  type: 'click' | 'form_submit' | 'page_view' | 'time_on_page' | 'scroll_depth' | 'custom_event' | 'revenue';
  target?: string; // Sélecteur CSS, URL, ou nom d'événement
  value?: number; // Valeur cible (temps en secondes, % de scroll, montant)
  isPrimary: boolean;
  description?: string;
}

export interface ABTestVariant {
  id: string;
  name: string;
  weight: number; // Pourcentage de trafic (0-100)
  config: Record<string, any>;
}

export interface ABTest {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  variants: ABTestVariant[];
  startDate?: Date;
  endDate?: Date;
  targetMetric: string;
  sampleSize?: number;
  goals: ConversionGoal[];
  alertsEnabled?: boolean;
  minSampleSize?: number;
  targetConfidence?: number;
}

export interface GoalConversion {
  goalId: string;
  value?: number;
  timestamp: Date;
}

export interface ABTestResult {
  testId: string;
  variantId: string;
  userId: string;
  timestamp: Date;
  converted: boolean;
  goalConversions: GoalConversion[];
  metadata?: Record<string, any>;
}

export interface StatisticalAlert {
  id: string;
  testId: string;
  testName: string;
  type: 'significance_reached' | 'winner_detected' | 'sample_size_reached' | 'confidence_threshold';
  message: string;
  timestamp: Date;
  read: boolean;
  data?: {
    winnerVariant?: string;
    conversionRate?: string;
    confidence?: string;
    sampleSize?: number;
  };
}

// Types d'objectifs prédéfinis
export const GOAL_TYPES = {
  click: {
    id: 'click',
    label: 'Clic sur élément',
    icon: 'ri-cursor-line',
    description: 'Mesure les clics sur un bouton ou lien spécifique',
    requiresTarget: true,
    targetPlaceholder: 'Ex: #cta-button, .signup-btn'
  },
  form_submit: {
    id: 'form_submit',
    label: 'Soumission de formulaire',
    icon: 'ri-file-list-line',
    description: 'Compte les soumissions de formulaire réussies',
    requiresTarget: true,
    targetPlaceholder: 'Ex: #contact-form, .newsletter-form'
  },
  page_view: {
    id: 'page_view',
    label: 'Vue de page',
    icon: 'ri-eye-line',
    description: 'Mesure les visites sur une page spécifique',
    requiresTarget: true,
    targetPlaceholder: 'Ex: /merci, /confirmation'
  },
  time_on_page: {
    id: 'time_on_page',
    label: 'Temps sur la page',
    icon: 'ri-time-line',
    description: 'Vérifie si l\'utilisateur reste X secondes',
    requiresValue: true,
    valuePlaceholder: 'Durée en secondes',
    valueUnit: 'secondes'
  },
  scroll_depth: {
    id: 'scroll_depth',
    label: 'Profondeur de scroll',
    icon: 'ri-arrow-down-line',
    description: 'Mesure si l\'utilisateur scrolle jusqu\'à X%',
    requiresValue: true,
    valuePlaceholder: 'Pourcentage (0-100)',
    valueUnit: '%'
  },
  custom_event: {
    id: 'custom_event',
    label: 'Événement personnalisé',
    icon: 'ri-code-line',
    description: 'Déclenché par un événement custom',
    requiresTarget: true,
    targetPlaceholder: 'Ex: video_played, add_to_cart'
  },
  revenue: {
    id: 'revenue',
    label: 'Revenu généré',
    icon: 'ri-money-euro-circle-line',
    description: 'Mesure le revenu moyen par utilisateur',
    requiresValue: false,
    valueUnit: '€'
  }
} as const;

class ABTestingManager {
  private readonly STORAGE_KEY = 'ab_tests';
  private readonly USER_VARIANT_KEY = 'ab_user_variants';
  private readonly RESULTS_KEY = 'ab_test_results';
  private readonly GOAL_TRACKING_KEY = 'ab_goal_tracking';
  private readonly ALERTS_KEY = 'ab_test_alerts';
  private readonly ALERT_HISTORY_KEY = 'ab_alert_history';

  // Obtenir tous les tests actifs
  getActiveTests(): ABTest[] {
    const tests = this.getAllTests();
    return tests.filter(test => test.status === 'running');
  }

  // Obtenir tous les tests
  getAllTests(): ABTest[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const tests = stored ? JSON.parse(stored) : [];
      // Assurer la compatibilité avec les anciens tests sans goals
      return tests.map((test: ABTest) => ({
        ...test,
        goals: test.goals || []
      }));
    } catch {
      return [];
    }
  }

  // Créer un nouveau test
  createTest(test: Omit<ABTest, 'id'>): ABTest {
    const newTest: ABTest = {
      ...test,
      id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      goals: test.goals || [],
      alertsEnabled: test.alertsEnabled !== false,
      minSampleSize: test.minSampleSize || 100,
      targetConfidence: test.targetConfidence || 95
    };

    const tests = this.getAllTests();
    tests.push(newTest);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tests));

    return newTest;
  }

  // Mettre à jour un test
  updateTest(testId: string, updates: Partial<ABTest>): void {
    const tests = this.getAllTests();
    const index = tests.findIndex(t => t.id === testId);
    
    if (index !== -1) {
      tests[index] = { ...tests[index], ...updates };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tests));
    }
  }

  // Ajouter un objectif à un test
  addGoal(testId: string, goal: Omit<ConversionGoal, 'id'>): ConversionGoal {
    const newGoal: ConversionGoal = {
      ...goal,
      id: `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    const tests = this.getAllTests();
    const test = tests.find(t => t.id === testId);
    
    if (test) {
      // Si c'est le premier objectif primaire, s'assurer qu'il n'y en a qu'un
      if (newGoal.isPrimary) {
        test.goals = test.goals.map(g => ({ ...g, isPrimary: false }));
      }
      test.goals.push(newGoal);
      this.updateTest(testId, { goals: test.goals });
    }

    return newGoal;
  }

  // Mettre à jour un objectif
  updateGoal(testId: string, goalId: string, updates: Partial<ConversionGoal>): void {
    const tests = this.getAllTests();
    const test = tests.find(t => t.id === testId);
    
    if (test) {
      const goalIndex = test.goals.findIndex(g => g.id === goalId);
      if (goalIndex !== -1) {
        // Si on définit cet objectif comme primaire, retirer le statut des autres
        if (updates.isPrimary) {
          test.goals = test.goals.map(g => ({ ...g, isPrimary: false }));
        }
        test.goals[goalIndex] = { ...test.goals[goalIndex], ...updates };
        this.updateTest(testId, { goals: test.goals });
      }
    }
  }

  // Supprimer un objectif
  removeGoal(testId: string, goalId: string): void {
    const tests = this.getAllTests();
    const test = tests.find(t => t.id === testId);
    
    if (test) {
      test.goals = test.goals.filter(g => g.id !== goalId);
      this.updateTest(testId, { goals: test.goals });
    }
  }

  // Supprimer un test
  deleteTest(testId: string): void {
    const tests = this.getAllTests();
    const filtered = tests.filter(t => t.id !== testId);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
  }

  // Assigner une variante à un utilisateur
  assignVariant(testId: string): string | null {
    const test = this.getAllTests().find(t => t.id === testId);
    if (!test || test.status !== 'running') return null;

    // Vérifier si l'utilisateur a déjà une variante assignée
    const userVariants = this.getUserVariants();
    if (userVariants[testId]) {
      return userVariants[testId];
    }

    // Assigner une nouvelle variante basée sur les poids
    const variantId = this.selectVariantByWeight(test.variants);
    
    // Sauvegarder l'assignation
    userVariants[testId] = variantId;
    localStorage.setItem(this.USER_VARIANT_KEY, JSON.stringify(userVariants));

    // Enregistrer l'impression initiale
    this.recordImpression(testId, variantId);

    return variantId;
  }

  // Enregistrer une impression
  private recordImpression(testId: string, variantId: string): void {
    const result: ABTestResult = {
      testId,
      variantId,
      userId: this.getUserId(),
      timestamp: new Date(),
      converted: false,
      goalConversions: []
    };

    const results = this.getResults();
    // Vérifier si une impression existe déjà pour cet utilisateur/test
    const existingIndex = results.findIndex(
      r => r.testId === testId && r.userId === result.userId
    );

    if (existingIndex === -1) {
      results.push(result);
      localStorage.setItem(this.RESULTS_KEY, JSON.stringify(results));
      
      // Vérifier les alertes après chaque nouvelle impression
      this.checkAndCreateAlerts(testId);
    }
  }

  // Sélectionner une variante selon les poids
  private selectVariantByWeight(variants: ABTestVariant[]): string {
    const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
    let random = Math.random() * totalWeight;

    for (const variant of variants) {
      random -= variant.weight;
      if (random <= 0) {
        return variant.id;
      }
    }

    return variants[0].id;
  }

  // Obtenir les variantes assignées à l'utilisateur
  getUserVariants(): Record<string, string> {
    try {
      const stored = localStorage.getItem(this.USER_VARIANT_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  // Obtenir la variante assignée pour un test spécifique
  getUserVariant(testId: string): string | null {
    const userVariants = this.getUserVariants();
    return userVariants[testId] || null;
  }

  // Obtenir la configuration d'une variante
  getVariantConfig(testId: string, variantId: string): Record<string, any> | null {
    const test = this.getAllTests().find(t => t.id === testId);
    if (!test) return null;

    const variant = test.variants.find(v => v.id === variantId);
    return variant?.config || null;
  }

  // Tracker un objectif spécifique
  trackGoal(testId: string, goalId: string, value?: number): void {
    const variantId = this.getUserVariant(testId);
    if (!variantId) return;

    const test = this.getAllTests().find(t => t.id === testId);
    if (!test) return;

    const goal = test.goals.find(g => g.id === goalId);
    if (!goal) return;

    const results = this.getResults();
    const userId = this.getUserId();
    const resultIndex = results.findIndex(
      r => r.testId === testId && r.userId === userId
    );

    if (resultIndex !== -1) {
      const goalConversion: GoalConversion = {
        goalId,
        value,
        timestamp: new Date()
      };

      // Vérifier si cet objectif n'a pas déjà été atteint
      const existingGoal = results[resultIndex].goalConversions.find(
        gc => gc.goalId === goalId
      );

      if (!existingGoal) {
        results[resultIndex].goalConversions.push(goalConversion);
        
        // Si c'est l'objectif primaire, marquer comme converti
        if (goal.isPrimary) {
          results[resultIndex].converted = true;
        }

        localStorage.setItem(this.RESULTS_KEY, JSON.stringify(results));
        
        // Vérifier les alertes après chaque conversion
        this.checkAndCreateAlerts(testId);
      }
    }
  }

  // Tracker par type d'objectif (pour faciliter l'utilisation)
  trackGoalByType(testId: string, goalType: ConversionGoal['type'], target?: string, value?: number): void {
    const test = this.getAllTests().find(t => t.id === testId);
    if (!test) return;

    const matchingGoal = test.goals.find(g => {
      if (g.type !== goalType) return false;
      if (target && g.target !== target) return false;
      return true;
    });

    if (matchingGoal) {
      this.trackGoal(testId, matchingGoal.id, value);
    }
  }

  // Enregistrer une conversion (méthode legacy pour compatibilité)
  trackConversion(testId: string, converted: boolean = true, metadata?: Record<string, any>): void {
    const variantId = this.getUserVariant(testId);
    if (!variantId) return;

    const results = this.getResults();
    const userId = this.getUserId();
    const resultIndex = results.findIndex(
      r => r.testId === testId && r.userId === userId
    );

    if (resultIndex !== -1) {
      results[resultIndex].converted = converted;
      results[resultIndex].metadata = metadata;
      localStorage.setItem(this.RESULTS_KEY, JSON.stringify(results));
      
      // Vérifier les alertes après chaque conversion
      this.checkAndCreateAlerts(testId);
    } else {
      const result: ABTestResult = {
        testId,
        variantId,
        userId,
        timestamp: new Date(),
        converted,
        goalConversions: [],
        metadata
      };
      results.push(result);
      localStorage.setItem(this.RESULTS_KEY, JSON.stringify(results));
      
      // Vérifier les alertes
      this.checkAndCreateAlerts(testId);
    }
  }

  // Obtenir tous les résultats
  getResults(): ABTestResult[] {
    try {
      const stored = localStorage.getItem(this.RESULTS_KEY);
      const results = stored ? JSON.parse(stored) : [];
      // Assurer la compatibilité avec les anciens résultats
      return results.map((r: ABTestResult) => ({
        ...r,
        goalConversions: r.goalConversions || []
      }));
    } catch {
      return [];
    }
  }

  // Obtenir les résultats d'un test spécifique
  getTestResults(testId: string): ABTestResult[] {
    return this.getResults().filter(r => r.testId === testId);
  }

  // Calculer les statistiques d'un objectif
  getGoalStatistics(testId: string, goalId: string) {
    const results = this.getTestResults(testId);
    const test = this.getAllTests().find(t => t.id === testId);
    
    if (!test) return null;

    const goal = test.goals.find(g => g.id === goalId);
    if (!goal) return null;

    const variantStats = test.variants.map(variant => {
      const variantResults = results.filter(r => r.variantId === variant.id);
      const goalConversions = variantResults.filter(r => 
        r.goalConversions.some(gc => gc.goalId === goalId)
      );
      
      const total = variantResults.length;
      const conversions = goalConversions.length;
      const conversionRate = total > 0 ? (conversions / total) * 100 : 0;

      // Calculer la valeur moyenne si applicable
      let avgValue = 0;
      if (goal.type === 'revenue' || goal.type === 'time_on_page') {
        const values = goalConversions
          .flatMap(r => r.goalConversions)
          .filter(gc => gc.goalId === goalId && gc.value !== undefined)
          .map(gc => gc.value as number);
        
        avgValue = values.length > 0 
          ? values.reduce((a, b) => a + b, 0) / values.length 
          : 0;
      }

      return {
        variantId: variant.id,
        variantName: variant.name,
        impressions: total,
        conversions,
        conversionRate: conversionRate.toFixed(2),
        avgValue: avgValue.toFixed(2)
      };
    });

    return {
      goalId,
      goalName: goal.name,
      goalType: goal.type,
      isPrimary: goal.isPrimary,
      variants: variantStats
    };
  }

  // Calculer les statistiques d'un test
  getTestStatistics(testId: string) {
    const results = this.getTestResults(testId);
    const test = this.getAllTests().find(t => t.id === testId);
    
    if (!test) return null;

    const variantStats = test.variants.map(variant => {
      const variantResults = results.filter(r => r.variantId === variant.id);
      const conversions = variantResults.filter(r => r.converted).length;
      const total = variantResults.length;
      const conversionRate = total > 0 ? (conversions / total) * 100 : 0;

      return {
        variantId: variant.id,
        variantName: variant.name,
        impressions: total,
        conversions,
        conversionRate: conversionRate.toFixed(2),
        confidence: this.calculateConfidence(variantResults, results)
      };
    });

    // Trouver la meilleure variante
    const bestVariant = variantStats.reduce((best, current) => 
      parseFloat(current.conversionRate) > parseFloat(best.conversionRate) ? current : best
    , variantStats[0]);

    // Statistiques par objectif
    const goalStats = test.goals.map(goal => this.getGoalStatistics(testId, goal.id));

    return {
      testId,
      testName: test.name,
      status: test.status,
      totalImpressions: results.length,
      totalConversions: results.filter(r => r.converted).length,
      variants: variantStats,
      winner: bestVariant,
      startDate: test.startDate,
      endDate: test.endDate,
      goals: goalStats
    };
  }

  // Calculer la confiance statistique (simplifié)
  private calculateConfidence(variantResults: ABTestResult[], allResults: ABTestResult[]): string {
    const variantConversions = variantResults.filter(r => r.converted).length;
    const variantTotal = variantResults.length;
    
    if (variantTotal < 30) return 'Données insuffisantes';
    
    const conversionRate = variantTotal > 0 ? variantConversions / variantTotal : 0;
    
    // Calcul simplifié de la confiance
    if (variantTotal >= 100 && conversionRate > 0.1) return '95%+';
    if (variantTotal >= 50) return '80-95%';
    return '< 80%';
  }

  // Calculer la confiance statistique avancée (Z-test)
  private calculateStatisticalSignificance(testId: string): {
    isSignificant: boolean;
    confidence: number;
    pValue: number;
    winnerVariantId: string | null;
  } {
    const results = this.getTestResults(testId);
    const test = this.getAllTests().find(t => t.id === testId);
    
    if (!test || test.variants.length < 2) {
      return { isSignificant: false, confidence: 0, pValue: 1, winnerVariantId: null };
    }

    // Calculer les stats pour chaque variante
    const variantStats = test.variants.map(variant => {
      const variantResults = results.filter(r => r.variantId === variant.id);
      const conversions = variantResults.filter(r => r.converted).length;
      const total = variantResults.length;
      const rate = total > 0 ? conversions / total : 0;
      
      return {
        variantId: variant.id,
        variantName: variant.name,
        conversions,
        total,
        rate
      };
    });

    // Trouver les deux meilleures variantes
    const sorted = [...variantStats].sort((a, b) => b.rate - a.rate);
    const best = sorted[0];
    const second = sorted[1];

    // Vérifier la taille minimale de l'échantillon
    const minSampleSize = test.minSampleSize || 100;
    if (best.total < minSampleSize || second.total < minSampleSize) {
      return { isSignificant: false, confidence: 0, pValue: 1, winnerVariantId: null };
    }

    // Calcul du Z-score (test de proportion)
    const p1 = best.rate;
    const p2 = second.rate;
    const n1 = best.total;
    const n2 = second.total;

    const pooledP = (best.conversions + second.conversions) / (n1 + n2);
    const se = Math.sqrt(pooledP * (1 - pooledP) * (1/n1 + 1/n2));
    
    if (se === 0) {
      return { isSignificant: false, confidence: 0, pValue: 1, winnerVariantId: null };
    }

    const zScore = Math.abs(p1 - p2) / se;

    // Calculer la p-value (approximation)
    const pValue = 2 * (1 - this.normalCDF(Math.abs(zScore)));
    
    // Calculer le niveau de confiance
    const confidence = (1 - pValue) * 100;

    // Vérifier si c'est statistiquement significatif
    const targetConfidence = test.targetConfidence || 95;
    const isSignificant = confidence >= targetConfidence && Math.abs(p1 - p2) > 0.01;

    return {
      isSignificant,
      confidence: Math.round(confidence * 100) / 100,
      pValue: Math.round(pValue * 10000) / 10000,
      winnerVariantId: isSignificant ? best.variantId : null
    };
  }

  // Fonction de distribution cumulative normale (approximation)
  private normalCDF(x: number): number {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989423 * Math.exp(-x * x / 2);
    const prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return x > 0 ? 1 - prob : prob;
  }

  // Vérifier et créer des alertes si nécessaire
  private checkAndCreateAlerts(testId: string): void {
    const test = this.getAllTests().find(t => t.id === testId);
    
    if (!test || !test.alertsEnabled || test.status !== 'running') {
      return;
    }

    const stats = this.getTestStatistics(testId);
    if (!stats) return;

    const significance = this.calculateStatisticalSignificance(testId);
    const alerts: StatisticalAlert[] = [];

    // Alerte : Significativité statistique atteinte
    if (significance.isSignificant && !this.hasAlertBeenSent(testId, 'significance_reached')) {
      const winnerVariant = test.variants.find(v => v.id === significance.winnerVariantId);
      
      alerts.push({
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        testId,
        testName: test.name,
        type: 'significance_reached',
        message: `🎯 Significativité statistique atteinte ! Le test "${test.name}" a atteint un niveau de confiance de ${significance.confidence}%.`,
        timestamp: new Date(),
        read: false,
        data: {
          confidence: `${significance.confidence}%`
        }
      });

      // Alerte : Gagnant détecté
      if (winnerVariant) {
        const winnerStats = stats.variants.find((v: any) => v.variantId === significance.winnerVariantId);
        
        alerts.push({
          id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          testId,
          testName: test.name,
          type: 'winner_detected',
          message: `🏆 Gagnant détecté ! La variante "${winnerVariant.name}" performe significativement mieux avec un taux de conversion de ${winnerStats?.conversionRate}%.`,
          timestamp: new Date(),
          read: false,
          data: {
            winnerVariant: winnerVariant.name,
            conversionRate: winnerStats?.conversionRate,
            confidence: `${significance.confidence}%`
          }
        });
      }
    }

    // Alerte : Taille d'échantillon minimale atteinte
    const minSampleSize = test.minSampleSize || 100;
    if (stats.totalImpressions >= minSampleSize && !this.hasAlertBeenSent(testId, 'sample_size_reached')) {
      alerts.push({
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        testId,
        testName: test.name,
        type: 'sample_size_reached',
        message: `📊 Taille d'échantillon atteinte ! Le test "${test.name}" a collecté ${stats.totalImpressions} impressions (objectif : ${minSampleSize}).`,
        timestamp: new Date(),
        read: false,
        data: {
          sampleSize: stats.totalImpressions
        }
      });
    }

    // Alerte : Seuil de confiance atteint
    const targetConfidence = test.targetConfidence || 95;
    if (significance.confidence >= targetConfidence && !this.hasAlertBeenSent(testId, 'confidence_threshold')) {
      alerts.push({
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        testId,
        testName: test.name,
        type: 'confidence_threshold',
        message: `✅ Seuil de confiance atteint ! Le test "${test.name}" a atteint ${significance.confidence}% de confiance (objectif : ${targetConfidence}%).`,
        timestamp: new Date(),
        read: false,
        data: {
          confidence: `${significance.confidence}%`
        }
      });
    }

    // Sauvegarder les nouvelles alertes
    if (alerts.length > 0) {
      this.saveAlerts(alerts);
    }
  }

  // Vérifier si une alerte a déjà été envoyée
  private hasAlertBeenSent(testId: string, alertType: StatisticalAlert['type']): boolean {
    try {
      const history = localStorage.getItem(this.ALERT_HISTORY_KEY);
      const sentAlerts: Array<{ testId: string; type: string }> = history ? JSON.parse(history) : [];
      return sentAlerts.some(a => a.testId === testId && a.type === alertType);
    } catch {
      return false;
    }
  }

  // Sauvegarder les alertes
  private saveAlerts(newAlerts: StatisticalAlert[]): void {
    try {
      // Ajouter aux alertes actuelles
      const currentAlerts = this.getAlerts();
      const allAlerts = [...currentAlerts, ...newAlerts];
      localStorage.setItem(this.ALERTS_KEY, JSON.stringify(allAlerts));

      // Ajouter à l'historique
      const history = localStorage.getItem(this.ALERT_HISTORY_KEY);
      const sentAlerts: Array<{ testId: string; type: string }> = history ? JSON.parse(history) : [];
      
      newAlerts.forEach(alert => {
        sentAlerts.push({ testId: alert.testId, type: alert.type });
      });
      
      localStorage.setItem(this.ALERT_HISTORY_KEY, JSON.stringify(sentAlerts));
    } catch {
      // Silently fail in production
    }
  }

  // Obtenir toutes les alertes
  getAlerts(): StatisticalAlert[] {
    try {
      const stored = localStorage.getItem(this.ALERTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // Obtenir les alertes non lues
  getUnreadAlerts(): StatisticalAlert[] {
    return this.getAlerts().filter(a => !a.read);
  }

  // Obtenir les alertes d'un test spécifique
  getTestAlerts(testId: string): StatisticalAlert[] {
    return this.getAlerts().filter(a => a.testId === testId);
  }

  // Marquer une alerte comme lue
  markAlertAsRead(alertId: string): void {
    const alerts = this.getAlerts();
    const alert = alerts.find(a => a.id === alertId);
    
    if (alert) {
      alert.read = true;
      localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
    }
  }

  // Marquer toutes les alertes comme lues
  markAllAlertsAsRead(): void {
    const alerts = this.getAlerts();
    alerts.forEach(a => a.read = true);
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
  }

  // Supprimer une alerte
  deleteAlert(alertId: string): void {
    const alerts = this.getAlerts().filter(a => a.id !== alertId);
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
  }

  // Supprimer toutes les alertes
  clearAllAlerts(): void {
    localStorage.removeItem(this.ALERTS_KEY);
  }

  // Forcer la vérification des alertes pour tous les tests actifs
  checkAllTestsForAlerts(): void {
    const activeTests = this.getActiveTests();
    activeTests.forEach(test => {
      this.checkAndCreateAlerts(test.id);
    });
  }

  // Obtenir les statistiques de significativité pour un test
  getSignificanceStats(testId: string) {
    return this.calculateStatisticalSignificance(testId);
  }

  // Obtenir ou créer un ID utilisateur unique
  private getUserId(): string {
    const key = 'ab_user_id';
    let userId = localStorage.getItem(key);
    
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(key, userId);
    }
    
    return userId;
  }

  // Réinitialiser les assignations utilisateur (pour tests)
  resetUserVariants(): void {
    localStorage.removeItem(this.USER_VARIANT_KEY);
  }

  // Exporter les résultats en CSV
  exportResultsToCSV(testId: string): string {
    const results = this.getTestResults(testId);
    const test = this.getAllTests().find(t => t.id === testId);
    
    const headers = ['Test ID', 'Variant ID', 'User ID', 'Timestamp', 'Converted', 'Goals Completed', 'Metadata'];
    
    const rows = results.map(r => [
      r.testId,
      r.variantId,
      r.userId,
      r.timestamp.toString(),
      r.converted.toString(),
      r.goalConversions.map(gc => {
        const goal = test?.goals.find(g => g.id === gc.goalId);
        return goal?.name || gc.goalId;
      }).join('; '),
      JSON.stringify(r.metadata || {})
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    return csv;
  }

  // Nettoyer les anciens résultats
  cleanOldResults(daysToKeep: number = 90): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    const results = this.getResults();
    const filtered = results.filter(r => new Date(r.timestamp) > cutoffDate);
    
    localStorage.setItem(this.RESULTS_KEY, JSON.stringify(filtered));
  }
}

export const abTestingManager = new ABTestingManager();

// Hook React pour utiliser l'A/B testing
export function useABTest(testId: string) {
  const variantId = abTestingManager.assignVariant(testId);
  const config = variantId ? abTestingManager.getVariantConfig(testId, variantId) : null;

  const trackConversion = (converted: boolean = true, metadata?: Record<string, any>) => {
    abTestingManager.trackConversion(testId, converted, metadata);
  };

  const trackGoal = (goalId: string, value?: number) => {
    abTestingManager.trackGoal(testId, goalId, value);
  };

  const trackGoalByType = (goalType: ConversionGoal['type'], target?: string, value?: number) => {
    abTestingManager.trackGoalByType(testId, goalType, target, value);
  };

  return {
    variantId,
    config,
    trackConversion,
    trackGoal,
    trackGoalByType,
    isActive: variantId !== null
  };
}

// Ajoute des métadonnées de cache à une réponse
function addCacheMetadata(response: Response): Response {
  const headers = new Headers(response.headers);
  headers.set('x-cache-timestamp', Date.now().toString());
  headers.set('x-cache-hit', 'true');

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
}

// Précharge une liste d'URLs dans le cache
export async function precacheUrls(
  urls: string[],
  cacheName: string
): Promise<void> {
  const cache = await caches.open(cacheName);
  
  await Promise.all(
    urls.map(async (url) => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          await cache.put(url, addCacheMetadata(response));
        }
      } catch {
        // Silently fail in production
      }
    })
  );
}
